import { Link } from "@/lib/router-compat";
import { ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import charlineImg from "@/assets/charline-portrait.png";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import TextReveal from "@/components/TextReveal";

const experiences = [
  { lieu: "Patriarche Architecture", annee: "2017", duree: "7 ans" },
  { lieu: "Chabanne Architecte", annee: "2016", duree: "1 an" },
  { lieu: "Agent Immobilier / Home Staging", annee: "2015", duree: "1 an" },
];

const diplomes = [
  { titre: "Architecte d'intérieur", annee: "2023" },
  { titre: "Couleur Architecture et Espace", annee: "2013" },
  { titre: "Designer Global", annee: "2012" },
];

const APropos = () => {
  return (
    <Layout>
      <SEOHead
        title="Architecte d'intérieur Lyon | JIIA Studio - Charline Jacob"
        description="JIIA Studio, architecte d'intérieur à Lyon. Je donne une âme aux espaces qui en sont dépourvus. Concept, couleur, mobilier sur mesure. Lyon et Rhône-Alpes."
        canonical="/a-propos"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Charline Jacob",
          jobTitle: "Architecte d'intérieur",
          worksFor: { "@type": "LocalBusiness", name: "JIIA Studio" },
          address: { "@type": "PostalAddress", addressLocality: "Grézieu-la-Varenne", addressRegion: "Auvergne-Rhône-Alpes", addressCountry: "FR" },
          image: "https://jiia-studio.com/og-jiia-studio.jpg",
          url: "https://jiia-studio.com/a-propos",
          knowsAbout: [
            "Architecture d'intérieur",
            "Design de concept",
            "Colorimétrie",
            "Mobilier sur mesure",
            "Aménagement résidentiel",
            "Aménagement professionnel",
            "Rénovation",
            "Décoration intérieure",
          ],
          alumniOf: [
            { "@type": "EducationalOrganization", name: "Diplôme d'architecte d'intérieur (2023)" },
            { "@type": "EducationalOrganization", name: "Master Couleur Architecture et Espace (2013)" },
            { "@type": "EducationalOrganization", name: "Designer Global (2012)" },
          ],
          hasOccupation: {
            "@type": "Occupation",
            name: "Architecte d'intérieur",
            occupationLocation: { "@type": "City", name: "Lyon" },
            skills: "Architecture d'intérieur, design de concept, colorimétrie, mobilier sur mesure, suivi de chantier",
          },
          sameAs: [
            "https://www.instagram.com/charline_archi_jiia_studio/",
            "https://www.linkedin.com/company/jiia-studio/",
          ],
        }}
      />

      {/* Hero */}
      <section className="py-24 md:py-32">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <AnimatedSection>
              <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-4">
                Architecte d'intérieur & designer de concept
              </p>
              <TextReveal>
                <h1 className="font-heading text-4xl md:text-5xl font-light mb-8 leading-tight">
                  Charline - Architecte d'intérieur et designer de concept à Lyon
                </h1>
              </TextReveal>
              <div className="space-y-5 text-muted-foreground font-body leading-relaxed">
                <p className="text-foreground font-heading text-lg italic">
                  Certains espaces marquent. Une couleur bien posée, une circulation fluide, un détail presque imperceptible mais qui fait toute la différence.
                </p>
                <p>
                  Je suis Charline, fondatrice de <strong className="text-foreground">JIIA Studio</strong>, architecte d'intérieur et designer de concept installée dans l'Ouest lyonnais. Avant de créer le studio, j'ai passé sept ans chez Patriarche puis un an chez Chabanne, sur des projets d'envergure où j'ai appris à lire un plan technique et à tenir un planning de chantier. Aujourd'hui je travaille pour des particuliers qui rénovent et pour des indépendants qui ouvrent un restaurant, un cabinet ou une boutique.
                </p>

                <h2 className="font-heading text-2xl md:text-3xl font-light text-foreground pt-4">Trois formations qui se complètent</h2>
                <p>
                  Designer global en 2012, Master Couleur Architecture et Espace en 2013, diplôme d'architecte d'intérieur en 2023. Ce sont trois manières différentes de regarder une pièce et je les utilise toutes les trois sur un même projet.
                </p>
                <p>
                  La couleur reste ma spécialité. Concrètement : savoir qu'un bleu profond mangera deux mètres carrés visuels dans une chambre au nord, ou qu'un blanc trop froid rendra un parquet chêne complètement jaune. Ce sont des choses qui se travaillent sur place, échantillons en main, pas sur un écran.
                </p>

                <h2 className="font-heading text-2xl md:text-3xl font-light text-foreground pt-4">Ma façon de travailler</h2>
                <p>
                  Je présente toujours deux options : celle que vous avez demandée et une seconde à laquelle vous n'aviez pas pensé. Parfois la première l'emporte, parfois non et souvent le projet final emprunte aux deux. C'est plus long à préparer, mais ça évite les projets tièdes.
                </p>
                <p>
                  Les contraintes ne me gênent pas. Un budget serré, une poutre impossible à déplacer ou un bail commercial qui interdit de toucher à la façade obligent à trouver des solutions plus intéressantes qu'une page blanche.
                </p>

                <h2 className="font-heading text-2xl md:text-3xl font-light text-foreground pt-4">Du premier croquis à la réception</h2>
                <p>
                  Concept, plans techniques, rendus 3D, déclaration préalable en mairie, consultation des artisans, visites de chantier, réception. Vous avez le même interlocuteur du début à la fin, ce qui évite les informations perdues entre deux prestataires.
                </p>
                <p className="text-foreground font-heading text-lg italic pt-2">
                  Vous avez un projet résidentiel ou professionnel à Lyon ou en région Rhône-Alpes ?{" "}
                  <Link to="/contact" className="underline underline-offset-4 hover:text-accent transition-colors not-italic">
                    Parlons-en
                  </Link>
                  .
                </p>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 mt-8 text-sm font-body uppercase tracking-widest text-foreground hover:text-accent transition-colors"
              >
                Me contacter <ArrowRight className="h-4 w-4" />
              </Link>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="aspect-[3/4] overflow-hidden rounded-sm">
                <img
                  src={charlineImg}
                  alt="Portrait de Charline Jacob, architecte d'intérieur"
                  className="w-full h-full object-cover object-top"
                  loading="eager"
                />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Parcours */}
      <section className="py-24 bg-secondary">
        <div className="container max-w-4xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            {/* Expériences */}
            <AnimatedSection>
              <h2 className="font-heading text-2xl md:text-3xl font-light mb-8">Expériences</h2>
              <div className="space-y-6">
                {experiences.map((exp, i) => (
                  <AnimatedSection key={exp.lieu} delay={i * 0.1}>
                    <div className="border-l-2 border-accent/30 pl-4">
                      <p className="font-heading text-lg text-foreground">{exp.lieu}</p>
                      <p className="text-sm text-muted-foreground font-body">
                        {exp.annee} · {exp.duree}
                      </p>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </AnimatedSection>

            {/* Diplômes */}
            <AnimatedSection delay={0.15}>
              <h2 className="font-heading text-2xl md:text-3xl font-light mb-8">Diplômes</h2>
              <div className="space-y-6">
                {diplomes.map((d, i) => (
                  <AnimatedSection key={d.titre} delay={0.15 + i * 0.1}>
                    <div className="border-l-2 border-accent/30 pl-4">
                      <p className="font-heading text-lg text-foreground">{d.titre}</p>
                      <p className="text-sm text-muted-foreground font-body">{d.annee}</p>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Internal links */}
      <section className="py-16 bg-background">
        <div className="container max-w-4xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link to="/prestations" className="group p-6 border border-border rounded-sm hover:border-accent transition-colors">
              <p className="text-xs font-body uppercase tracking-widest text-muted-foreground mb-2">Prestations</p>
              <p className="font-heading text-lg group-hover:text-accent transition-colors">Découvrir mes services</p>
              <p className="text-sm text-muted-foreground font-body mt-1">Architecture d'intérieur, conseils déco, images 3D…</p>
            </Link>
            <Link to="/projets" className="group p-6 border border-border rounded-sm hover:border-accent transition-colors">
              <p className="text-xs font-body uppercase tracking-widest text-muted-foreground mb-2">Portfolio</p>
              <p className="font-heading text-lg group-hover:text-accent transition-colors">Voir mes réalisations</p>
              <p className="text-sm text-muted-foreground font-body mt-1">Projets résidentiels et professionnels en Rhône-Alpes.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-primary text-primary-foreground">
        <div className="container text-center max-w-2xl">
          <AnimatedSection direction="none" scale>
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-6">
              Parlons de votre projet
            </h2>
            <p className="text-primary-foreground/70 font-body mb-8">
              Premier rendez-vous offert pour échanger sur vos envies et vos besoins.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 px-8 py-4 border border-primary-foreground/30 text-sm font-body uppercase tracking-widest hover:bg-primary-foreground hover:text-primary transition-all duration-500"
            >
              Prendre rendez-vous <ArrowRight className="h-4 w-4" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
};

export default APropos;
